<template>
    <div v-if="visible" class="fixed inset-0 z-[3000] flex items-center justify-center bg-black/70">
        <div class="relative bg-[#22242D] rounded-[20px] p-5 min-w-[400px] max-w-[500px] mx-4 shadow-2xl" @click.stop>
            <button @click="handleClose"
                class="absolute top-4 right-4 w-6 h-6 flex items-center justify-center text-white hover:scale-105 hover:rotate-180 transition-all duration-200">
                <img src="@/assets/images/common/_close.webp" alt="close" class="w-[24px]" />
            </button>

            <div class="flex flex-col gap-5 justify-center items-center">
                <h3 class="max-w-[260px] text-center text-2xl font-medium text-white">
                    {{ t('batchTools.confirmDelete') }}
                </h3>
                <p class="text-sub-title text-sm leading-relaxed">
                    {{ t('batchTools.deleteTitle', { total: delete_total }) }}
                </p>
                <div class="grid grid-cols-2 gap-5">
                    <button
                        class="md:min-w-[120px] px-5 py-[10px] rounded-[8px] flex items-center justify-center border border-white/20 text-white text-base font-medium hover:border-white/50 transition-all duration-200"
                        @click="handleClose">
                        {{ t('batchTools.deleteCancel') }}
                    </button>
                    <button
                        class="md:min-w-[120px] bg-create-btn px-5 py-[10px] rounded-[8px] flex items-center justify-center border border-white/20 text-black text-base font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-create-btn/80 transition-all duration-200"
                        :disabled="delete_loading" @click="handleDelete">
                        <el-icon v-if="delete_loading" class="animate-spin mx-1">
                            <Loading />
                        </el-icon>
                        {{ t('batchTools.deleteConfirm') }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useI18n } from '~/composables/useI18n'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { emitter } from '~/utils/emitter'
import { useUserStore } from '~/stores/user'
import {
    deleteBatchGenerateRecordApi,
    deleteBatchGenerateRecordBatchApi,
} from '~/api/create'
import { noticeMessagePopup } from '~/utils/common'
import { Loading } from '@element-plus/icons-vue'
const userStore = useUserStore()
const visible = ref<boolean>(false)
const delete_loading = ref<boolean>(false)
const { t } = useI18n()

/**
 * 处理关闭弹窗
 */
const handleClose = () => {
    visible.value = false
}

const delete_data = ref<any>(null)
const delete_type = ref<'all' | 'single'>('all')
const delete_item_list = ref<any>([])
const delete_total = ref<number>(0)
// 获取删除数量
const getDeleteTotal = () => {

    if (delete_type.value === 'all') {
        return 1
    } else {
        // 获取删除的素材数量
        let total = 0
        delete_item_list.value?.forEach((item: any) => {
            let length_ = item.result_urls?.length || 1
            total += length_
        })

        return total
    }
}

const showVisible = (data: any, type: 'all' | 'single') => {
    if (type === 'all') {
        delete_data.value = data
    } else {
        delete_item_list.value = data
    }
    delete_type.value = type
    delete_total.value = getDeleteTotal()

    visible.value = true
}

const handleDelete = async () => {
    if (delete_loading.value) return
    delete_loading.value = true
    let data_: any = {
        items: [],
    }

    if (delete_type.value === 'all') {
        data_.id = delete_data.value.id
        delete data_.items
    } else {
        data_.items = delete_item_list.value
    }

    try {
        const res =
            delete_type.value === 'all'
                ? await deleteBatchGenerateRecordApi(data_)
                : await deleteBatchGenerateRecordBatchApi(data_)
        if (res.status === 200) {
            // 先保存要发送的数据，因为 handleClose 会触发 watch 清空数据
            const emitData = {
                type: delete_type.value,
                data: delete_type.value === 'all' ? delete_data.value : delete_item_list.value
            }
            handleClose()
            noticeMessagePopup(
                t('batchTools.deleteSuccess'),
                t('common.tip'),
                'success',
                3000
            )
            emitter.emit('reset-select-item-list')
            // 通知组件从本地移除被删除的项目，不重新请求接口
            emitter.emit('remove-deleted-items', emitData)
        }
    } catch (error) {
        console.error(error)
    } finally {
        delete_loading.value = false
    }
}

onMounted(async () => {
    emitter.on(
        'show-delete-confirm-modal',
        ({ item, type }: { item: any; type: 'all' | 'single' }) =>
            showVisible(item, type)
    )
})

onUnmounted(() => {
    emitter.off(
        'show-delete-confirm-modal',
        ({ item, type }: { item: any; type: 'all' | 'single' }) =>
            showVisible(item, type)
    )
})

watch(
    () => visible.value,
    newVal => {
        if (!newVal) {
            delete_data.value = null
            delete_type.value = 'all'
            delete_item_list.value = []
        }
    }
)
</script>

<style scoped lang="less">
/* 自定义动画 */
@keyframes fadeIn {
    from {
        opacity: 0;
        transform: scale(0.9);
    }

    to {
        opacity: 1;
        transform: scale(1);
    }
}

.fixed {
    animation: fadeIn 0.3s ease-out;
}
</style>
